/**
 * useInteractionPreferences
 *
 * Read-only accessor for Human-Centered AI interaction preferences, so chat and
 * agent surfaces can adapt behavior (suggest vs auto-apply, pacing, always-explain)
 * without importing jotai atoms directly.
 *
 * Dependencies:
 * - atoms/interactionPreferencesAtoms
 */

'use client'

import { useAtomValue } from 'jotai'

import {
  interactionPreferencesAtom,
  type InteractionPreferences
} from '@/atoms/interactionPreferencesAtoms'

export interface UseInteractionPreferencesResult extends InteractionPreferences {
  /** Present AI actions as suggestions the user confirms. */
  prefersSuggestions: boolean
  /** Reveal multi-step output one step at a time. */
  prefersStepByStep: boolean
  /** Always surface reasoning and uncertainty. */
  prefersAlwaysExplain: boolean
}

export function useInteractionPreferences(): UseInteractionPreferencesResult {
  const prefs = useAtomValue(interactionPreferencesAtom)

  return {
    ...prefs,
    prefersSuggestions: prefs.suggestionMode === 'suggest',
    prefersStepByStep: prefs.pacing === 'stepByStep',
    prefersAlwaysExplain: prefs.explainability === 'always'
  }
}
